/**
 * Centralised booking status configuration.
 *
 * Single source of truth for booking status:
 *   - human-readable label (English fallback; i18n key alongside)
 *   - Tailwind badge classes (light + dark mode aware, sufficient contrast)
 *   - Tailwind block classes (for backgrounds with border, e.g. Booking Details modal)
 *   - hex color for chart libraries (Recharts PieChart, etc.)
 *   - lucide-react icon name (consumers import the icon themselves)
 *   - isActive flag (false = booking is "dead": cancelled/refunded/no_show)
 *
 * Used by: user Dashboard, BookingDetailsDialog, AdminDashboardPage, CMSPayments,
 * CMSReports, and any future surface that renders a booking status badge.
 */

export const BOOKING_STATUSES = {
  confirmed: {
    label: 'Confirmed',
    i18nKey: 'dashboard.confirmed',
    icon: 'CheckCircle',
    badge: 'bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30',
    block: 'bg-green-50 border border-green-200 dark:bg-green-950/40 dark:border-green-800 text-green-900 dark:text-green-100',
    iconColor: 'text-green-600 dark:text-green-400',
    borderLeft: '#22c55e',
    hex: '#22c55e',
    isActive: true,
  },
  pending_payment: {
    label: 'Pending Payment',
    i18nKey: 'dashboard.pendingPayment',
    icon: 'Clock',
    badge: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30',
    block: 'bg-amber-50 border border-amber-200 dark:bg-amber-950/40 dark:border-amber-800 text-amber-900 dark:text-amber-100',
    iconColor: 'text-amber-600 dark:text-amber-400',
    borderLeft: '#f59e0b',
    hex: '#f59e0b',
    isActive: true,
  },
  confirmation_pending: {
    label: 'Confirmation Pending',
    i18nKey: 'dashboard.confirmationPending',
    icon: 'Clock',
    badge: 'bg-orange-500/15 text-orange-700 dark:text-orange-400 border border-orange-500/30',
    block: 'bg-orange-50 border border-orange-200 dark:bg-orange-950/40 dark:border-orange-800 text-orange-900 dark:text-orange-100',
    iconColor: 'text-orange-600 dark:text-orange-400',
    borderLeft: '#ea580c',
    hex: '#ea580c',
    isActive: true,
  },
  completed: {
    label: 'Completed',
    i18nKey: 'dashboard.completed',
    icon: 'CheckCircle',
    badge: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/30',
    block: 'bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800 text-blue-900 dark:text-blue-100',
    iconColor: 'text-blue-600 dark:text-blue-400',
    borderLeft: '#3b82f6',
    hex: '#3b82f6',
    isActive: true,
  },
  cancelled: {
    label: 'Cancelled',
    i18nKey: 'dashboard.cancelled',
    icon: 'XCircle',
    badge: 'bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30',
    block: 'bg-red-50 border border-red-200 dark:bg-red-950/40 dark:border-red-800 text-red-900 dark:text-red-100',
    iconColor: 'text-red-600 dark:text-red-400',
    borderLeft: '#ef4444',
    hex: '#ef4444',
    isActive: false,
  },
  cancellation_requested: {
    label: 'Cancellation Requested',
    i18nKey: 'dashboard.cancellationRequested',
    icon: 'AlertTriangle',
    badge: 'bg-red-500/10 text-red-700 dark:text-red-300 border border-red-500/20',
    block: 'bg-red-50 border border-red-200 dark:bg-red-950/30 dark:border-red-800/60 text-red-900 dark:text-red-100',
    iconColor: 'text-red-500 dark:text-red-300',
    borderLeft: '#f87171',
    hex: '#f87171',
    isActive: false,
  },
  refunded: {
    label: 'Refunded',
    i18nKey: 'dashboard.refunded',
    icon: 'RotateCcw',
    badge: 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border border-purple-500/30',
    block: 'bg-purple-50 border border-purple-200 dark:bg-purple-950/40 dark:border-purple-800 text-purple-900 dark:text-purple-100',
    iconColor: 'text-purple-600 dark:text-purple-400',
    borderLeft: '#8b5cf6',
    hex: '#8b5cf6',
    isActive: false,
  },
  no_show: {
    label: 'No Show',
    i18nKey: 'dashboard.noShow',
    icon: 'XCircle',
    badge: 'bg-gray-500/15 text-gray-700 dark:text-gray-300 border border-gray-500/30',
    block: 'bg-gray-100 border border-gray-300 dark:bg-gray-800/60 dark:border-gray-700 text-gray-900 dark:text-gray-100',
    iconColor: 'text-gray-600 dark:text-gray-400',
    borderLeft: '#6b7280',
    hex: '#6b7280',
    isActive: false,
  },
  // Payment-specific statuses (used by /cms/payments which lists raw payment intents).
  paid: {
    label: 'Paid',
    i18nKey: 'cms.paid',
    icon: 'CheckCircle',
    badge: 'bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30',
    block: 'bg-green-50 border border-green-200 dark:bg-green-950/40 dark:border-green-800 text-green-900 dark:text-green-100',
    iconColor: 'text-green-600 dark:text-green-400',
    borderLeft: '#22c55e',
    hex: '#22c55e',
    isActive: true,
  },
  partially_refunded: {
    label: 'Partial Refund',
    i18nKey: 'cms.partiallyRefunded',
    icon: 'RotateCcw',
    badge: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border border-yellow-500/30',
    block: 'bg-yellow-50 border border-yellow-200 dark:bg-yellow-950/40 dark:border-yellow-800 text-yellow-900 dark:text-yellow-100',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
    borderLeft: '#eab308',
    hex: '#eab308',
    isActive: true,
  },
  failed: {
    label: 'Failed',
    i18nKey: 'cms.failed',
    icon: 'XCircle',
    badge: 'bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30',
    block: 'bg-red-50 border border-red-200 dark:bg-red-950/40 dark:border-red-800 text-red-900 dark:text-red-100',
    iconColor: 'text-red-600 dark:text-red-400',
    borderLeft: '#ef4444',
    hex: '#ef4444',
    isActive: false,
  },
};

// Fallback for unknown/missing status — neutral slate, never blank
const UNKNOWN_STATUS = {
  label: 'Unknown',
  i18nKey: 'dashboard.statusUnknown',
  icon: 'Clock',
  badge: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border border-slate-500/30',
  block: 'bg-slate-100 border border-slate-300 dark:bg-slate-800/60 dark:border-slate-700 text-foreground',
  iconColor: 'text-slate-600 dark:text-slate-400',
  borderLeft: '#94a3b8',
  hex: '#94a3b8',
  isActive: false,
};

/** Return the status metadata for a given status string. Falls back to neutral. */
export const getStatusMeta = (status) => {
  if (!status) return UNKNOWN_STATUS;
  return BOOKING_STATUSES[status] || {
    ...UNKNOWN_STATUS,
    label: String(status).replace(/_/g, ' '),
  };
};

/** Is this booking still "live" (not cancelled/refunded/no_show)? */
export const isActiveBooking = (b) => {
  if (!b || !b.status) return true; // optimistic for legacy records without status
  const meta = BOOKING_STATUSES[b.status];
  return meta ? meta.isActive : true;
};

/** Lookup hex color for chart segments by status name. */
export const getStatusHex = (status) => getStatusMeta(status).hex;
